import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { EnvironmentProviders, makeEnvironmentProviders } from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { ActivatedRouteSnapshot, DetachedRouteHandle, RouteReuseStrategy } from '@angular/router';
import { TOOLS_SERVICE } from 'src/engine-sdk/contract/tools';
import { USER_INTERACTION_SERVICE } from 'src/engine-sdk/contract/user-interaction';
import { environment } from '../environments/environment';
import { authInterceptor } from './auth/providers/auth-interceptor.provider';
import { httpErrorsInterceptor } from './errors/interceptors/http-errors.interceptor';
import { busyIndicatorInterceptor } from './layout/components/busy-indicator/busy-indicator.interceptor';
import { API_BASE_URL } from './services/api-clients';
import { ContextService } from './services/context.service';
import { CONTEXT_SERVICE } from './services/context.service.token';
import { LodashService } from './services/lodash.service';
import { ToolsService } from './services/tools-service';
import { UserInteractionService } from './services/user-interaction.service';

import { provideAuth } from './auth/auth.providers';
import { provideEngineCulture } from './engine-culture/engine-culture.providers';
import { provideEngineForms } from './engine-forms/engine-forms.providers';
import { provideEngineOData } from './engine-odata/engine-odata.providers';
import { provideEngineProcesses } from './engine-processes/engine-processes.providers';
import { provideEngineTranslations } from './engine-translations/engine-translations.providers';
import { provideErrorHandling } from './errors/errors.providers';
import { provideKendoCore } from './kendo-core/kendo-core.providers';
import { provideLayout } from './layout/layout.providers';
import { provideEngineMetadata } from './metadata/engine-metadata.providers';
import { provideNavigation } from './navigation/navigation.providers';
import { provideNotification } from './notification/notification.providers';
import { provideOwlDateTime } from './owl-core/owl-core.providers';
import { provideScriptsRunning } from './scripts-running/scripts-running.providers';
import { provideSharedServices } from './shared.providers';
import { provideAppStore } from './store.providers';
import { provideUser } from './user/user.providers';

export const odataBaseUrl = environment.urls.BaseUrl;

class NoRouteReuseStrategy implements RouteReuseStrategy {
  shouldDetach(_route: ActivatedRouteSnapshot): boolean {
    return false;
  }

  store(_route: ActivatedRouteSnapshot, _handle: DetachedRouteHandle | null): void {}

  shouldAttach(_route: ActivatedRouteSnapshot): boolean {
    return false;
  }

  retrieve(_route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    return null;
  }

  shouldReuseRoute(_future: ActivatedRouteSnapshot, _curr: ActivatedRouteSnapshot): boolean {
    return false;
  }
}

function provideCoreServices(): EnvironmentProviders {
  return makeEnvironmentProviders([
    { provide: API_BASE_URL, useValue: odataBaseUrl },
    ContextService,
    { provide: CONTEXT_SERVICE, useExisting: ContextService },
    ToolsService,
    { provide: TOOLS_SERVICE, useExisting: ToolsService },
    LodashService,
    UserInteractionService,
    { provide: USER_INTERACTION_SERVICE, useExisting: UserInteractionService },
    { provide: RouteReuseStrategy, useClass: NoRouteReuseStrategy },
    provideHttpClient(withInterceptors([httpErrorsInterceptor, authInterceptor, busyIndicatorInterceptor])),
  ]);
}

export function provideEngineCore(): EnvironmentProviders {
  return makeEnvironmentProviders([
    provideAnimations(),
    provideCoreServices(),
    provideAppStore(),
    provideSharedServices(),

    provideAuth(),
    provideErrorHandling(),
    provideEngineCulture(),
    provideKendoCore(),
    provideOwlDateTime(),
    provideNavigation(),
    provideEngineTranslations(),
    provideEngineForms(),
    provideEngineOData(),
    provideEngineMetadata(),
    provideNotification(),
    provideLayout(),
    provideEngineProcesses(),
    provideScriptsRunning(),
    provideUser(),
  ]);
}
